import { Column } from '../Dynamic';
import { Account } from '../account/Account';
import { Stock } from './Stock';
import { SaleItem } from './SaleItem';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient, authenticatedFetch } from '../../api/apiClient';
import { checkResponse } from '../../util/ResponseHelper';
import { BASE_API, BASE_PUBLIC_API } from '../../config/applicationConfig';
import { PaymentMethodType } from './PaymentMethod';
import { PageRequest, PaginatedResponse } from '../Api';
import { StreamInterface } from '../stream/Stream';
const URL = BASE_API + '/sale';

export enum SaleStatus {
  CANCELLED = 'CANCELLED',
  SUBMITTED = 'SUBMITTED',
  PAYED = 'PAYED',
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
}

export interface Sale {
  id?: number;
  publicId?: string;
  created?: string;
  createdByEMail?: string;
  updated?: string;
  updatedByEMail?: string;
  token?: string;
  saleStatus: SaleStatus;
  account: Account;
  stock: Stock;
  accountName: string;
  accountEMail: string;
  saleItems: SaleItem[];
  accountInAction?: Account;
  memberSell: boolean;
  closedAmount?: number;
  paidAmount?: number;
  calculatedAmount?: number;
  calculatedIncludedTax?: number;
  // Payment method information
  paymentMethod?: PaymentMethodType;
  paymentReference?: string;
}

export const saleColumns: Column<Sale>[] = [
  {
    id: 'token',
    label: 'Token',
    inputType: 'text',
    gridWidth: 6,
  },
  {
    id: 'saleStatus',
    label: 'Status',
    inputType: 'select',
    gridWidth: 6,
  },
  {
    id: 'accountName',
    label: 'Kunde',
    inputType: 'text',
    gridWidth: 6,
  },
  {
    id: 'accountEMail',
    label: 'Kunden E-Mail',
    inputType: 'text',
    gridWidth: 6,
  },
  {
    id: 'memberSell',
    label: 'Mitgliederverkauf',
    inputType: 'checkbox',
    gridWidth: 6,
  },
  {
    id: 'paymentMethod',
    label: 'Zahlungsmethode',
    inputType: 'select',
    options: [],
    gridWidth: 6,
  },
  {
    id: 'paymentReference',
    label: 'Zahlungsreferenz',
    inputType: 'text',
    gridWidth: 6,
  },
  {
    id: 'closedAmount',
    label: 'Geschlossener Betrag',
    inputType: 'number',
    gridWidth: 6,
  },
  {
    id: 'paidAmount',
    label: 'Bezahlter Betrag',
    inputType: 'number',
    gridWidth: 6,
  },
  {
    id: 'calculatedAmount',
    label: 'Berechneter Betrag',
    inputType: 'number',
    gridWidth: 6,
  },
  {
    id: 'calculatedIncludedTax',
    label: 'Enthaltene Steuer',
    inputType: 'number',
    gridWidth: 6,
  },
  // System fields (read-only)
  {
    id: 'id',
    label: 'ID',
    minWidth: 80,
    disabled: true,
  },
  {
    id: 'publicId',
    label: 'Öffentliche ID',
    inputType: 'text',
    minWidth: 120,
    disabled: true,
  },
  {
    id: 'created',
    label: 'Erstellt',
    inputType: 'datetime',
    minWidth: 150,
    sortable: true,
    disabled: true,
  },
  {
    id: 'createdByEMail',
    label: 'Erstellt von',
    inputType: 'text',
    minWidth: 150,
    sortable: true,
    disabled: true,
  },
  {
    id: 'updated',
    label: 'Geändert',
    inputType: 'datetime',
    minWidth: 150,
    sortable: true,
    disabled: true,
  },
  {
    id: 'updatedByEMail',
    label: 'Geändert von',
    inputType: 'text',
    minWidth: 150,
    sortable: true,
    disabled: true,
  },
];

export const SALE_QUERY_KEYS = {
  all: ['sales'] as const,
  get: (publicId: string) => [...SALE_QUERY_KEYS.all, 'get', publicId] as const,
  next10: (id: string) => [...SALE_QUERY_KEYS.all, 'next10', id] as const,
};

export const fetchNextSales = async (stockId: string) => {
  return checkResponse<Sale[]>(await apiClient.get(URL + `/on-stock/${stockId}`));
};

export const useSales = (stockId: string) => {
  return useQuery({
    queryKey: SALE_QUERY_KEYS.next10(stockId),
    queryFn: () => fetchNextSales(stockId),

    enabled: !!stockId,
  });
};

export const saveSale = async (sale: Sale) => {
  return checkResponse<Sale>(await apiClient.post(URL, sale));
};

export const updateSale = async (sale: Sale) => {
  return checkResponse<Sale>(await apiClient.put(URL, sale));
};

export const useSalePost = () => {
  return useMutation({
    mutationFn: async (sale: Sale) => {
      return await saveSale(sale);
    },
  });
};

export const useSalePut = () => {
  return useMutation({
    mutationFn: async (sale: Sale) => {
      return await updateSale(sale);
    },
  });
};

export const useSale = (saleId: string) => {
  const params = new URLSearchParams();
  params.append('publicId', saleId);
  return useQuery({
    queryKey: SALE_QUERY_KEYS.get(saleId),
    queryFn: async () => {
      return checkResponse<Sale>(await apiClient.get(URL + `?${params.toString()}`));
    },
    enabled: !!saleId,
  });
};

export const getUserSales = async (pageRequest: PageRequest): Promise<PaginatedResponse<Sale>> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('pageNo', pageRequest.pageNo.toString());
    queryParams.append('pageSize', pageRequest.pageSize.toString());
    queryParams.append('sortDir', pageRequest.sortDir);
    queryParams.append('sortBy', pageRequest.sortBy);
    const response = await authenticatedFetch(`${BASE_API}/sale/user?${queryParams.toString()}`);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching streams:', error);
    throw error;
  }
};
